import {
  For,
  Show,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  onMount,
} from "solid-js";

import { IS_DEV, useClient } from "@revolt/client";
import { useNavigate } from "@revolt/routing";
import { Portal } from "solid-js/web";
import { useState } from "@revolt/state";
import { CalculationMethod, Coordinates, Madhab, PrayerTimes } from "adhan";

/**
 * Majalis home screen for returning members.
 *
 * - next seminar / live now (from /majalis-data/seminars.json, kept in sync
 *   with the Google Calendar by the schedule bot)
 * - continue where you left off, mentions, unread channels
 * - latest announcement, suggested room, community pulse
 */

type Seminar = {
  id: string;
  title: string;
  educator?: string;
  start: string;
  minutes?: number;
  channel?: string;
};

const SCHEDULE_URL = IS_DEV
  ? "https://82-29-184-55.sslip.io/majalis-data/seminars.json"
  : "/majalis-data/seminars.json";

const SUPPORT_URL = IS_DEV
  ? "https://82-29-184-55.sslip.io/support"
  : "/support";

const HELP_CATS = [
  {
    id: "membership",
    label: "Membership & billing",
    tip: "After you pay, access is usually updated within a few minutes. If it has been longer, open a ticket and tell us the email you used on Buy Me a Coffee.",
  },
  {
    id: "access",
    label: "Can't access a seminar",
    tip: "Join links are shown to Members. Lite members can see the schedule, but not the link. If you are a Member and can't see it, open a ticket.",
  },
  {
    id: "technical",
    label: "Technical problem",
    tip: "Try a hard refresh first (Ctrl+Shift+R, or close and reopen the app). If it still happens, tell us what you were doing.",
  },
  { id: "other", label: "Something else", tip: "" },
] as const;

type PrayerCfg = {
  lat: number;
  lng: number;
  label: string;
  tz?: string;
  method: string;
  madhab: "standard" | "hanafi";
};

const PRAYER_KEY = "majalis:prayer";

const PRAYER_CITIES = [
  { name: "Mecca", lat: 21.4225, lng: 39.8262, tz: "Asia/Riyadh" },
  { name: "Medina", lat: 24.4672, lng: 39.6024, tz: "Asia/Riyadh" },
  { name: "Riyadh", lat: 24.7136, lng: 46.6753, tz: "Asia/Riyadh" },
  { name: "Dubai", lat: 25.2048, lng: 55.2708, tz: "Asia/Dubai" },
  { name: "Doha", lat: 25.2854, lng: 51.531, tz: "Asia/Qatar" },
  { name: "Cairo", lat: 30.0444, lng: 31.2357, tz: "Africa/Cairo" },
  { name: "Istanbul", lat: 41.0082, lng: 28.9784, tz: "Europe/Istanbul" },
  { name: "Amman", lat: 31.9454, lng: 35.9284, tz: "Asia/Amman" },
  { name: "Baghdad", lat: 33.3152, lng: 44.3661, tz: "Asia/Baghdad" },
  { name: "Karachi", lat: 24.8607, lng: 67.0011, tz: "Asia/Karachi" },
  { name: "Lahore", lat: 31.5204, lng: 74.3587, tz: "Asia/Karachi" },
  { name: "Delhi", lat: 28.6139, lng: 77.209, tz: "Asia/Kolkata" },
  { name: "Mumbai", lat: 19.076, lng: 72.8777, tz: "Asia/Kolkata" },
  { name: "Dhaka", lat: 23.8103, lng: 90.4125, tz: "Asia/Dhaka" },
  { name: "Kuala Lumpur", lat: 3.139, lng: 101.6869, tz: "Asia/Kuala_Lumpur" },
  { name: "Singapore", lat: 1.3521, lng: 103.8198, tz: "Asia/Singapore" },
  { name: "Jakarta", lat: -6.2088, lng: 106.8456, tz: "Asia/Jakarta" },
  { name: "Casablanca", lat: 33.5731, lng: -7.5898, tz: "Africa/Casablanca" },
  { name: "Lagos", lat: 6.5244, lng: 3.3792, tz: "Africa/Lagos" },
  { name: "Nairobi", lat: -1.2921, lng: 36.8219, tz: "Africa/Nairobi" },
  { name: "Johannesburg", lat: -26.2041, lng: 28.0473, tz: "Africa/Johannesburg" },
  { name: "London", lat: 51.5072, lng: -0.1276, tz: "Europe/London" },
  { name: "Birmingham", lat: 52.4862, lng: -1.8904, tz: "Europe/London" },
  { name: "Manchester", lat: 53.4808, lng: -2.2426, tz: "Europe/London" },
  { name: "Paris", lat: 48.8566, lng: 2.3522, tz: "Europe/Paris" },
  { name: "Berlin", lat: 52.52, lng: 13.405, tz: "Europe/Berlin" },
  { name: "New York", lat: 40.7128, lng: -74.006, tz: "America/New_York" },
  { name: "Washington DC", lat: 38.9072, lng: -77.0369, tz: "America/New_York" },
  { name: "Atlanta", lat: 33.749, lng: -84.388, tz: "America/New_York" },
  { name: "Chicago", lat: 41.8781, lng: -87.6298, tz: "America/Chicago" },
  { name: "Houston", lat: 29.7604, lng: -95.3698, tz: "America/Chicago" },
  { name: "Los Angeles", lat: 34.0522, lng: -118.2437, tz: "America/Los_Angeles" },
  { name: "Toronto", lat: 43.6532, lng: -79.3832, tz: "America/Toronto" },
  { name: "Sydney", lat: -33.8688, lng: 151.2093, tz: "Australia/Sydney" },
  { name: "Melbourne", lat: -37.8136, lng: 144.9631, tz: "Australia/Melbourne" },
];

const PRAYER_METHODS: [string, string][] = [
  ["MuslimWorldLeague", "Muslim World League"],
  ["NorthAmerica", "ISNA (North America)"],
  ["UmmAlQura", "Umm al-Qura (Makkah)"],
  ["Egyptian", "Egyptian General Authority"],
  ["Karachi", "Univ. of Islamic Sciences, Karachi"],
  ["Turkey", "Diyanet (Turkey)"],
  ["Dubai", "Dubai"],
  ["Kuwait", "Kuwait"],
  ["Qatar", "Qatar"],
  ["Singapore", "Singapore"],
  ["MoonsightingCommittee", "Moonsighting Committee"],
];

const defaultMethod = (tz: string) => {
  if (/^America\//.test(tz)) return "NorthAmerica";
  if (/^Asia\/(Riyadh|Kuwait|Qatar|Bahrain|Aden)$/.test(tz)) return "UmmAlQura";
  if (/^Asia\/(Karachi|Dhaka|Kolkata|Calcutta)$/.test(tz)) return "Karachi";
  if (tz === "Africa/Cairo") return "Egyptian";
  if (tz === "Europe/Istanbul") return "Turkey";
  if (tz === "Asia/Dubai") return "Dubai";
  if (tz === "Asia/Singapore" || tz === "Asia/Kuala_Lumpur" || tz === "Asia/Jakarta") return "Singapore";
  return "MuslimWorldLeague";
};

const prayerCalc = (cfg: PrayerCfg, date: Date) => {
  const make = (CalculationMethod as any)[cfg.method] ?? CalculationMethod.MuslimWorldLeague;
  const params = make();
  if (cfg.madhab === "hanafi" && (Madhab as any).Hanafi !== undefined) {
    params.madhab = (Madhab as any).Hanafi;
  }
  return new PrayerTimes(new Coordinates(cfg.lat, cfg.lng), date, params);
};

const countdown = (ms: number) => {
  const m = Math.max(0, Math.round(ms / 60000));
  const h = Math.floor(m / 60);
  return h > 0 ? h + "h " + (m % 60) + "m" : m + " min";
};

const SELECT =
  "width:100%;box-sizing:border-box;padding:11px 12px;border-radius:12px;border:1px solid #2a2a33;background:#101015;color:#fff;font-size:14px;font-family:inherit;";

const CARD_IDS = [
  "continue",
  "seminar",
  "comingup",
  "prayer",
  "announcement",
  "catchup",
  "foryou",
  "help",
] as const;
const CARD_COL: Record<string, number> = {
  continue: 0,
  seminar: 0,
  comingup: 0,
  prayer: 1,
  announcement: 1,
  catchup: 1,
  foryou: 1,
  help: 1,
};
const LAYOUT_KEY = "majalis:layout";
type CardLayout = { order: string[]; hidden: string[] };

const TOOL_BTN =
  "display:flex;align-items:center;justify-content:center;height:28px;min-width:28px;padding:0 10px;box-sizing:border-box;border-radius:999px;background:#23232b;border:1px solid #3a3a45;color:#e6e6ee;font-size:13px;font-weight:600;font-family:inherit;cursor:pointer;";

const SURVEY_KEY = "majalis_survey";
const LAST_CHANNEL_KEY = "majalis:lastChannel";
const B32 = "0123456789ABCDEFGHJKMNPQRSTVWXYZ";

const RESET =
  "appearance:none;font:inherit;color:inherit;text-align:left;cursor:pointer;width:100%;box-sizing:border-box;";
const CARD =
  "display:flex;flex-direction:column;gap:10px;padding:18px;border-radius:18px;background:#1a1a20;border:1px solid #2a2a33;";
const OVER =
  "font-size:11px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:#E2B04A;";
const HEAD =
  "font-size:12px;font-weight:700;letter-spacing:0.14em;text-transform:uppercase;color:#a4a4b2;";
const MUTED = "font-size:13px;color:#a4a4b2;";
const GOLD_BTN =
  "display:block;width:100%;box-sizing:border-box;text-align:center;padding:15px 20px;border-radius:13px;border:none;background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;font-size:16px;font-weight:700;cursor:pointer;";

function ulidTime(id: string): number {
  let t = 0;
  for (const ch of id.slice(0, 10).toUpperCase()) {
    const i = B32.indexOf(ch);
    if (i < 0) return 0;
    t = t * 32 + i;
  }
  return t;
}

function ago(ms: number): string {
  const s = Math.max(0, Date.now() - ms) / 1000;
  if (s < 60) return "just now";
  const m = Math.floor(s / 60);
  if (m < 60) return m + "m ago";
  const h = Math.floor(m / 60);
  if (h < 24) return h + "h ago";
  return Math.floor(h / 24) + "d ago";
}

function until(ms: number): string {
  const total = Math.max(0, Math.round(ms / 60000));
  const d = Math.floor(total / 1440);
  const h = Math.floor((total % 1440) / 60);
  const m = total % 60;
  if (d > 0) return `In ${d} day${d > 1 ? "s" : ""}${h ? `, ${h} hr${h > 1 ? "s" : ""}` : ""}`;
  if (h > 0) return `In ${h} hr${h > 1 ? "s" : ""}${m ? `, ${m} min` : ""}`;
  return `In ${m} min`;
}

function norm(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, "");
}

function escapeRe(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

// Majalis: browser install prompt (kept so the small install link can open it)
let installEvent: any = null;
const [canPrompt, setCanPrompt] = createSignal(false);
if (typeof window !== "undefined") {
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    installEvent = e;
    setCanPrompt(true);
  });
  window.addEventListener("appinstalled", () => {
    installEvent = null;
    setCanPrompt(false);
  });
}

/* eslint-disable @typescript-eslint/no-explicit-any */
export function Dashboard() {
  const client = useClient();
  const navigate = useNavigate();

  // Already running as an installed app? Then hide the install link.
  const installed = (() => {
    try {
      return (
        window.matchMedia("(display-mode: standalone)").matches ||
        window.matchMedia("(display-mode: fullscreen)").matches ||
        window.matchMedia("(display-mode: minimal-ui)").matches ||
        (navigator as any).standalone === true
      );
    } catch {
      return false;
    }
  })();
  const ua = navigator.userAgent;
  const isApple = /iphone|ipad|ipod/i.test(ua);
  const isAndroid = /android/i.test(ua);
  const isEdge = /edg\//i.test(ua);
  const isChromium = /chrome|chromium/i.test(ua);
  const howTo = isApple
    ? "Tap Share, then Add to Home Screen."
    : isAndroid
      ? "Tap \u22EE, then Install app."
      : isEdge || isChromium
        ? "Click the install icon at the right of the address bar."
        : "Open Majalis in Chrome or Edge to install it.";
  const [showHowTo, setShowHowTo] = createSignal(false);

  // ---- support tickets ----
  const [helpOpen, setHelpOpen] = createSignal(false);
  const [helpCat, setHelpCat] = createSignal<string>("");
  const [helpMsg, setHelpMsg] = createSignal("");
  const [helpState, setHelpState] = createSignal<"idle" | "sending" | "done" | "error">("idle");
  const [helpError, setHelpError] = createSignal("");
  const [helpTicket, setHelpTicket] = createSignal<{ number: number; channel: string } | null>(null);
  const [myTickets, setMyTickets] = createSignal<{ number: number; channel: string; label: string }[]>([]);

  const helpHeaders = (): Record<string, string> => {
    const h: any = (client() as any)?.authenticationHeader;
    const out: Record<string, string> = { "content-type": "application/json" };
    if (h) out[h[0]] = h[1];
    return out;
  };

  const fetchMine = () => {
    fetch(SUPPORT_URL + "/mine", { headers: helpHeaders() })
      .then((r) => (r.ok ? r.json() : { tickets: [] }))
      .then((j) => setMyTickets(j.tickets ?? []))
      .catch(() => {});
  };
  onMount(fetchMine);

  const closeHelp = () => {
    setHelpOpen(false);
    if (helpState() === "done") {
      setHelpMsg("");
      setHelpCat("");
    }
    setHelpState("idle");
    setHelpError("");
  };

  // Majalis: lock page scroll and close on Esc while the help form is open
  createEffect(() => {
    if (!helpOpen()) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") closeHelp();
    };
    window.addEventListener("keydown", onKey);
    onCleanup(() => {
      document.body.style.overflow = prev;
      window.removeEventListener("keydown", onKey);
    });
  });

  const openTicketChannel = (channel: string) => {
    const server: any = (client()?.servers.toList() ?? [])[0];
    if (server) navigate(`/server/${server.id}/channel/${channel}`);
    closeHelp();
  };

  const submitTicket = async () => {
    if (helpState() === "sending") return;
    setHelpState("sending");
    setHelpError("");
    try {
      const r = await fetch(SUPPORT_URL + "/ticket", {
        method: "POST",
        headers: helpHeaders(),
        body: JSON.stringify({ category: helpCat(), message: helpMsg() }),
      });
      const j: any = await r.json().catch(() => ({}));
      if (!r.ok) {
        setHelpError(j.error || "We couldn't open your ticket just now. Please try again.");
        setHelpState("error");
        return;
      }
      setHelpTicket({ number: j.number, channel: j.channel });
      setHelpState("done");
      fetchMine();
    } catch {
      setHelpError("Can't reach support right now. Check your connection and try again.");
      setHelpState("error");
    }
  };

  const install = async () => {
    if (installEvent) {
      const ev = installEvent;
      installEvent = null;
      setCanPrompt(false);
      try {
        ev.prompt();
        await ev.userChoice;
      } catch {}
    } else {
      setShowHowTo((v) => !v);
    }
  };
  const appState = useState();
  const openMajalis = () => {
    // Land in #general (the busiest room); fall back to the server page
    const general = findChannel(/^[\W_]*general([\W_]*chat)?[\W_]*$/i);
    if (general) return go(general);
    const server: any = (client()?.servers.toList() ?? [])[0];
    if (server) navigate(`/server/${server.id}`);
  };

  const [seminars, setSeminars] = createSignal<Seminar[]>([]);
  const [loaded, setLoaded] = createSignal(false);
  const [now, setNow] = createSignal(Date.now());
  const [announcement, setAnnouncement] = createSignal<{
    text: string;
    at: number;
    channel: any;
  } | null>(null);
  const [pulse, setPulse] = createSignal<{ members: number; online: number } | null>(null);
  const [topics, setTopics] = createSignal<string[]>([]);

  const go = (c: any) =>
    navigate(c.serverId ? `/server/${c.serverId}/channel/${c.id}` : `/channel/${c.id}`);

  const findChannel = (pattern: RegExp, preferVoice = false): any => {
    for (const server of client()?.servers.toList() ?? []) {
      if (preferVoice) {
        const v = server.channels.find(
          (c: any) => pattern.test(c.name ?? "") && c.isVoice,
        );
        if (v) return v;
      }
    }
    for (const server of client()?.servers.toList() ?? []) {
      const c = server.channels.find((c: any) => pattern.test(c.name ?? ""));
      if (c) return c;
    }
    return undefined;
  };
  const openByName = (pattern: RegExp, preferVoice = false) => {
    const c = findChannel(pattern, preferVoice);
    if (c) go(c);
  };

  /* ---------- data ---------- */

  async function loadSchedule() {
    try {
      const r = await fetch(SCHEDULE_URL, { cache: "no-store" });
      if (r.ok) {
        const j = await r.json();
        setSeminars(Array.isArray(j.seminars) ? j.seminars : []);
      }
    } catch (err) {
      console.warn("[majalis] schedule load failed", err);
    }
    setLoaded(true);
  }

  async function loadAnnouncement() {
    const c = findChannel(/announce/i);
    if (!c) return;
    try {
      const msgs = (await (client() as any).api.get(`/channels/${c.id}/messages`, {
        limit: 1,
      })) as any[];
      const m = msgs?.[0];
      if (m?.content) setAnnouncement({ text: m.content, at: ulidTime(m._id), channel: c });
    } catch (err) {
      console.warn("[majalis] announcement load failed", err);
    }
  }

  async function loadPulse() {
    const server = (client()?.servers.toList() ?? [])[0];
    if (!server) return;
    try {
      const r = (await (client() as any).api.get(`/servers/${server.id}/members`)) as any;
      setPulse({
        members: r.members.length,
        online: r.users.filter((u: any) => u.online).length,
      });
    } catch (err) {
      console.warn("[majalis] pulse load failed", err);
    }
  }

  async function loadTopics() {
    try {
      const res = (await (client() as any).api.post("/sync/settings/fetch", {
        keys: [SURVEY_KEY],
      })) as Record<string, [number, string]>;
      const raw = res?.[SURVEY_KEY]?.[1];
      if (raw) {
        const j = JSON.parse(raw);
        if (Array.isArray(j.topics)) setTopics(j.topics);
      }
    } catch {
      /* no survey saved: that's fine */
    }
  }

  onMount(() => {
    loadSchedule();
    loadAnnouncement();
    loadPulse();
    loadTopics();
    const clock = setInterval(() => setNow(Date.now()), 30_000);
    const refresh = setInterval(loadSchedule, 60_000);
    const slow = setInterval(() => {
      loadAnnouncement();
      loadPulse();
    }, 300_000);
    onCleanup(() => {
      clearInterval(clock);
      clearInterval(refresh);
      clearInterval(slow);
    });
  });

  /* ---------- derived ---------- */

  const parsed = createMemo(() =>
    seminars()
      .map((s) => {
        const t = Date.parse(s.start);
        return { ...s, t, end: t + (s.minutes ?? 90) * 60000 };
      })
      .filter((s) => !isNaN(s.t) && s.end > now())
      .sort((a, b) => a.t - b.t),
  );
  const live = createMemo(() =>
    parsed().find((s) => now() >= s.t - 5 * 60000 && now() < s.end),
  );
  const next = createMemo(() => live() ?? parsed()[0]);
  const later = createMemo(() => parsed().filter((s) => s !== next()).slice(0, 3));

  const myName = () => {
    const u: any = client()?.user;
    let n = "";
    try {
      const server: any = (client()?.servers.toList() ?? [])[0];
      n = server?.member?.nickname ?? "";
    } catch {}
    n = n || u?.displayName || u?.username || "";
    return n;
  };

  const lastChannel = createMemo(() => {
    try {
      const id = localStorage.getItem(LAST_CHANNEL_KEY);
      return id ? (client()?.channels.get(id) as any) : undefined;
    } catch {
      return undefined;
    }
  });

  const allChannels = () => (client()?.channels.toList() ?? []) as any[];

  const mentionTotal = createMemo(() =>
    allChannels().reduce((n, c) => n + (c.mentions?.size ?? 0), 0),
  );
  const mentionChannel = createMemo(() => allChannels().find((c) => (c.mentions?.size ?? 0) > 0));

  const unreadList = createMemo(() =>
    allChannels()
      .filter((c) => {
        try {
          return c.unread && !c.muted && c.type !== "SavedMessages";
        } catch {
          return false;
        }
      })
      .sort((a, b) => (b.lastMessageAt?.getTime() ?? 0) - (a.lastMessageAt?.getTime() ?? 0))
      .slice(0, 5)
      .map((c) => ({ c, name: c.name ?? "Direct message", mentions: c.mentions?.size ?? 0 })),
  );

  const suggested = createMemo(() => {
    for (const topic of topics()) {
      const t = norm(topic);
      if (t.length < 4) continue;
      for (const server of client()?.servers.toList() ?? []) {
        const c = server.channels.find((c: any) => {
          const n = norm(c.name ?? "");
          return n.length >= 4 && (n.includes(t) || t.includes(n));
        });
        if (c) return { channel: c as any, topic };
      }
    }
    return undefined;
  });

  const plan = createMemo(() => {
    try {
      const server: any = (client()?.servers.toList() ?? [])[0];
      const ids: string[] = server?.member?.roles ?? [];
      const names = ids.map((id) => server.roles?.get(id)?.name).filter(Boolean) as string[];
      return names.find((n) => /member|lite/i.test(n));
    } catch {
      return undefined;
    }
  });

  const when = (t: number) => {
    const d = new Date(t);
    return {
      mon: d.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
      day: String(d.getDate()),
      weekday: d.toLocaleDateString(undefined, { weekday: "short" }),
      time: d.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" }),
    };
  };

  // Members see the Member Schedule channel (it holds the Meet link);
  // Lite members can't see it, so they only get the Lite Schedule channel.
  const memberSchedule = () => findChannel(/member[’'‘]?s?[’'‘]?[\s_-]*schedule/i);
  const canJoin = () => !!memberSchedule();
  const openSchedule = () => {
    const c =
      memberSchedule() ??
      findChannel(/lite[\s_-]*schedule/i) ??
      findChannel(/^(?!.*admin).*schedule/i) ??
      findChannel(/schedule/i);
    if (c) go(c);
  };

  // ---- prayer times ----
  const loadPrayer = (): PrayerCfg | null => {
    try {
      const v = localStorage.getItem(PRAYER_KEY);
      return v ? (JSON.parse(v) as PrayerCfg) : null;
    } catch {
      return null;
    }
  };
  const [prayerCfg, setPrayerCfg] = createSignal<PrayerCfg | null>(loadPrayer());
  const [prayerOpen, setPrayerOpen] = createSignal(false);
  const [prayerBusy, setPrayerBusy] = createSignal(false);
  const [prayerErr, setPrayerErr] = createSignal("");

  const savePrayer = (c: PrayerCfg | null) => {
    setPrayerCfg(c);
    try {
      if (c) localStorage.setItem(PRAYER_KEY, JSON.stringify(c));
      else localStorage.removeItem(PRAYER_KEY);
    } catch {
      /* storage unavailable */
    }
  };

  const useMyLocation = () => {
    if (!navigator.geolocation) {
      setPrayerErr("This browser can't share your location. Please choose a city.");
      return;
    }
    setPrayerBusy(true);
    setPrayerErr("");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
        savePrayer({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          label: tz.split("/").pop()!.replace(/_/g, " ") || "Your location",
          method: prayerCfg()?.method ?? defaultMethod(tz),
          madhab: prayerCfg()?.madhab ?? "standard",
        });
        setPrayerBusy(false);
        setPrayerOpen(false);
      },
      () => {
        setPrayerBusy(false);
        setPrayerErr("Location is blocked. Allow it in your browser, or choose a city.");
      },
      { timeout: 10000, maximumAge: 3600000 },
    );
  };

  const chooseCity = (name: string) => {
    const c = PRAYER_CITIES.find((x) => x.name === name);
    if (!c) return;
    savePrayer({
      lat: c.lat,
      lng: c.lng,
      label: c.name,
      tz: c.tz,
      method: prayerCfg()?.method ?? defaultMethod(c.tz),
      madhab: prayerCfg()?.madhab ?? "standard",
    });
    setPrayerErr("");
    setPrayerOpen(false);
  };

  const setPrayerOpt = (k: "method" | "madhab", v: string) => {
    const c = prayerCfg();
    if (c) savePrayer({ ...c, [k]: v } as PrayerCfg);
  };

  const prayers = createMemo(() => {
    const cfg = prayerCfg();
    if (!cfg) return null;
    try {
      const n = now();
      const today = prayerCalc(cfg, new Date(n));
      const list = [
        { key: "Fajr", t: today.fajr.getTime() },
        { key: "Sunrise", t: today.sunrise.getTime() },
        { key: "Dhuhr", t: today.dhuhr.getTime() },
        { key: "Asr", t: today.asr.getTime() },
        { key: "Maghrib", t: today.maghrib.getTime() },
        { key: "Isha", t: today.isha.getTime() },
      ];
      const five = list.filter((p) => p.key !== "Sunrise");
      let nextP = five.find((p) => p.t > n);
      if (!nextP) {
        const tm = prayerCalc(cfg, new Date(n + 86400000));
        nextP = { key: "Fajr", t: tm.fajr.getTime() };
      }
      return { list, next: nextP };
    } catch {
      return null;
    }
  });

  const prayerTime = (t: number) =>
    new Date(t).toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      timeZone: prayerCfg()?.tz,
    });

  const hijri = () => {
    try {
      return new Intl.DateTimeFormat("en-u-ca-islamic-umalqura", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(new Date(now()));
    } catch {
      return "";
    }
  };

  // ---- customizable layout ----
  const loadLayout = (): CardLayout => {
    try {
      const v = JSON.parse(localStorage.getItem(LAYOUT_KEY) || "null");
      const ids = CARD_IDS as readonly string[];
      const saved: string[] = Array.isArray(v?.order) ? v.order.filter((x: string) => ids.includes(x)) : [];
      const order = [...saved, ...ids.filter((x) => !saved.includes(x))];
      const hidden: string[] = Array.isArray(v?.hidden) ? v.hidden.filter((x: string) => ids.includes(x)) : [];
      return { order, hidden };
    } catch {
      return { order: [...CARD_IDS], hidden: [] };
    }
  };
  const [layout, setLayout] = createSignal<CardLayout>(loadLayout());
  const [editing, setEditing] = createSignal(false);
  const [dragId, setDragId] = createSignal("");

  // Majalis: cards glide to their new position when the layout changes
  const flip = (change: () => void) => {
    const before = new Map<string, DOMRect>();
    document.querySelectorAll<HTMLElement>(".mj-card[data-card]").forEach((el) => {
      before.set(el.dataset.card!, el.getBoundingClientRect());
    });
    change();
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    document.querySelectorAll<HTMLElement>(".mj-card[data-card]").forEach((el) => {
      const b = before.get(el.dataset.card!);
      if (!b || b.width === 0) return;
      const a = el.getBoundingClientRect();
      if (a.width === 0) return;
      const dx = b.left - a.left;
      const dy = b.top - a.top;
      if (!dx && !dy) return;
      el.animate(
        [{ transform: "translate(" + dx + "px, " + dy + "px)" }, { transform: "none" }],
        { duration: 380, easing: "cubic-bezier(0.22, 1, 0.36, 1)" },
      );
    });
  };

  const saveLayout = (l: CardLayout) => {
    flip(() => setLayout(l));
    try {
      localStorage.setItem(LAYOUT_KEY, JSON.stringify(l));
    } catch {
      /* storage unavailable */
    }
  };

  const moveCard = (id: string, dir: -1 | 1) => {
    const o = [...layout().order];
    const i = o.indexOf(id);
    let j = i + dir;
    while (j >= 0 && j < o.length && CARD_COL[o[j]] !== CARD_COL[id]) j += dir;
    if (j < 0 || j >= o.length) return;
    [o[i], o[j]] = [o[j], o[i]];
    saveLayout({ ...layout(), order: o });
  };

  const dropCard = (target: string) => {
    const id = dragId();
    setDragId("");
    if (!id || id === target || CARD_COL[id] !== CARD_COL[target]) return;
    const o = layout().order.filter((x) => x !== id);
    o.splice(o.indexOf(target), 0, id);
    saveLayout({ ...layout(), order: o });
  };

  const toggleCard = (id: string) => {
    const h = layout().hidden;
    saveLayout({ ...layout(), hidden: h.includes(id) ? h.filter((x) => x !== id) : [...h, id] });
  };

  const resetLayout = () => saveLayout({ order: [...CARD_IDS], hidden: [] });

  const cardStyle = (id: string) => {
    const hidden = layout().hidden.includes(id);
    let s = "position:relative;order:" + (layout().order.indexOf(id) + 1) + ";";
    if (hidden && !editing()) s += "display:none;";
    if (hidden && editing()) s += "opacity:.35;";
    if (editing()) s += "outline:1px dashed #5a4716;outline-offset:6px;border-radius:20px;cursor:grab;";
    return s;
  };

  const tools = (id: string) => (
    <Show when={editing()}>
      <div class="mj-ov" style="position:absolute;inset:0;z-index:4;" />
      <div class="mj-tools" style="position:absolute;top:-20px;right:14px;z-index:5;display:flex;gap:6px;">
        <button onClick={() => moveCard(id, -1)} aria-label="Move up" style={TOOL_BTN}>
          ↑
        </button>
        <button onClick={() => moveCard(id, 1)} aria-label="Move down" style={TOOL_BTN}>
          ↓
        </button>
        <button onClick={() => toggleCard(id)} style={TOOL_BTN}>
          {layout().hidden.includes(id) ? "Show" : "Hide"}
        </button>
      </div>
    </Show>
  );

  /* ---------- view ---------- */

  return (
    <div
      style="width:100%;min-height:100%;box-sizing:border-box;background:transparent;color:#fff;padding:36px 18px 64px;"
    >
      <style>{"@keyframes mjpulse{0%{opacity:1}50%{opacity:.3}100%{opacity:1}}.mj-wrap{max-width:560px;margin:0 auto;display:flex;flex-direction:column;gap:20px}.mj-col{display:flex;flex-direction:column;gap:20px;min-width:0}@media(min-width:900px){.mj-wrap{max-width:1080px;display:grid;grid-template-columns:minmax(0,1.4fr) minmax(0,1fr);gap:24px 28px;align-items:start}.mj-full{grid-column:1/-1}}@keyframes mjdrop{0%{opacity:0;transform:translateY(-34px) scale(.97)}60%{opacity:1;transform:translateY(5px) scale(1)}100%{opacity:1;transform:none}}.mj-wrap>.mj-full,.mj-col>*{animation:mjdrop .6s cubic-bezier(.22,1,.36,1) both}.mj-wrap>.mj-full:first-child{animation-delay:0s}.mj-col:nth-of-type(1)>*:nth-child(1){animation-delay:.10s}.mj-col:nth-of-type(1)>*:nth-child(2){animation-delay:.18s}.mj-col:nth-of-type(1)>*:nth-child(3){animation-delay:.26s}.mj-col:nth-of-type(1)>*:nth-child(4){animation-delay:.34s}.mj-col:nth-of-type(2)>*:nth-child(1){animation-delay:.18s}.mj-col:nth-of-type(2)>*:nth-child(2){animation-delay:.26s}.mj-col:nth-of-type(2)>*:nth-child(3){animation-delay:.34s}.mj-col:nth-of-type(2)>*:nth-child(4){animation-delay:.42s}.mj-wrap>.mj-full:not(:first-child){animation-delay:.5s}@media(prefers-reduced-motion:reduce){.mj-wrap>.mj-full,.mj-col>*{animation:none}}.mj-card:not(:has(> :not(.mj-tools):not(.mj-ov))){display:none}.mj-col>:not(.mj-card){order:0}"}</style>
      <div class="mj-wrap">
        {/* greeting */}
        <div class="mj-full" style="display:flex;flex-direction:column;gap:4px;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
            <div style="font-size:11px;font-weight:700;letter-spacing:0.22em;text-transform:uppercase;color:#E2B04A;">
              Majalis
            </div>
            <div style="display:flex;align-items:center;gap:8px;">
              <Show when={editing()}>
                <button
                  onClick={resetLayout}
                  style={RESET + "width:auto;display:flex;padding:5px 12px;border-radius:999px;background:#1f1f26;border:1px solid #2a2a33;color:#c9c9d3;font-size:12px;font-weight:600;"}
                >
                  Reset
                </button>
              </Show>
              <button
                onClick={() => setEditing(!editing())}
                style={
                  RESET +
                  "width:auto;display:flex;padding:5px 12px;border-radius:999px;font-size:12px;font-weight:700;" +
                  (editing()
                    ? "background:#2a2210;border:1px solid #FFC62E;color:#FFC62E;"
                    : "background:#1f1f26;border:1px solid #2a2a33;color:#c9c9d3;")
                }
              >
                {editing() ? "Done" : "Customize"}
              </button>
            </div>
          </div>
          <h1 style="margin:0;font-size:26px;font-weight:700;line-height:1.15;letter-spacing:-0.015em;">
            Asalamu Alaykum{myName() ? ", " + myName() : ""}
          </h1>
          <Show when={mentionTotal() > 0}>
            <button
              onClick={() => mentionChannel() && go(mentionChannel())}
              style={
                RESET +
                "width:auto;display:flex;align-items:center;gap:8px;background:none;border:none;padding:2px 0;font-size:14px;font-weight:600;color:#FFC62E;"
              }
            >
              <span style="width:8px;height:8px;border-radius:50%;background:#FFC62E;" aria-hidden="true" />
              <span>
                {mentionTotal() === 1
                  ? "1 person mentioned or replied to you"
                  : mentionTotal() + " mentions and replies waiting for you"}
              </span>
            </button>
          </Show>
          <button
            onClick={openMajalis}
            style={
              RESET +
              "width:auto;align-self:flex-start;display:flex;align-items:center;gap:8px;margin-top:12px;padding:10px 16px;border-radius:999px;background:#241d0c;border:1px solid #5a4716;color:#FFC62E;font-size:14px;font-weight:700;"
            }
          >
            <span>Open Majalis</span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M5 12h14M13 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        <div class="mj-col">
        {/* continue */}
        <div class="mj-card" data-card="continue" style={cardStyle("continue")} draggable={editing()} onDragStart={() => setDragId("continue")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("continue")}>
          {tools("continue")}
        <Show when={lastChannel()}>
          <button
            onClick={() => go(lastChannel())}
            style={RESET + "display:flex;align-items:center;gap:14px;padding:16px 16px 16px 18px;border-radius:18px;background:#1a1a20;border:1px solid #2a2a33;"}
          >
            <div style="flex-grow:1;display:flex;flex-direction:column;gap:5px;">
              <div style={OVER}>Continue where you left off</div>
              <div style="font-size:18px;font-weight:600;"># {lastChannel().name}</div>
              <Show when={lastChannel().lastMessageAt}>
                <div style={MUTED}>Latest message {ago(lastChannel().lastMessageAt.getTime())}</div>
              </Show>
            </div>
            <Chevron />
          </button>
        </Show>
        </div>

        {/* seminar hero */}
        <div class="mj-card" data-card="seminar" style={cardStyle("seminar")} draggable={editing()} onDragStart={() => setDragId("seminar")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("seminar")}>
          {tools("seminar")}
        <Show
          when={next()}
          fallback={
            <Show when={loaded()}>
              <div style={CARD}>
                <div style={OVER}>Seminars</div>
                <div style="font-size:18px;font-weight:600;">No seminars scheduled yet</div>
                <div style={MUTED}>New sessions are posted in the schedule channel.</div>
                <button onClick={openSchedule} style={GOLD_BTN}>
                  Open schedule
                </button>
              </div>
            </Show>
          }
        >
          <Show
            when={live()}
            fallback={
              <div style="display:flex;flex-direction:column;gap:14px;padding:20px;border-radius:22px;background:linear-gradient(160deg,#2a2210,#1a1a20 70%);border:1px solid #6b551a;box-shadow:0 0 34px rgba(255,184,28,0.08);">
                <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
                  <div style={OVER}>Next seminar</div>
                  <div style="padding:6px 12px;border-radius:999px;background:#3a2e0f;color:#FFC62E;font-size:12px;font-weight:700;">
                    {until(next()!.t - now())}
                  </div>
                </div>
                <div style="display:flex;flex-direction:column;gap:6px;">
                  <div style="font-size:23px;font-weight:700;line-height:1.2;letter-spacing:-0.01em;">
                    {next()!.title}
                  </div>
                  <Show when={next()!.educator}>
                    <div style="font-size:15px;color:#c9c9d3;">with {next()!.educator}</div>
                  </Show>
                </div>
                <div style="display:flex;align-items:center;gap:10px;font-size:14px;color:#e6e6ee;">
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#E2B04A" stroke-width="1.8" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <rect x="3.5" y="5" width="17" height="15.5" rx="2.5" />
                    <path d="M3.5 10h17" />
                    <path d="M8 3v4" />
                    <path d="M16 3v4" />
                  </svg>
                  <span>
                    {when(next()!.t).weekday} {when(next()!.t).day} {when(next()!.t).mon.charAt(0) + when(next()!.t).mon.slice(1).toLowerCase()} · {when(next()!.t).time}
                  </span>
                  <span style="color:#a4a4b2;">(your time)</span>
                </div>
                <button onClick={openSchedule} style={GOLD_BTN}>
                  Open schedule
                </button>
              </div>
            }
          >
            <div style="display:flex;flex-direction:column;gap:14px;padding:20px;border-radius:22px;background:linear-gradient(160deg,#2c1613,#1a1a20 70%);border:1px solid #ff6b5a;">
              <div style="display:flex;align-items:center;gap:10px;">
                <div style="width:10px;height:10px;border-radius:50%;background:#ff6b5a;animation:mjpulse 1.4s infinite;" aria-hidden="true" />
                <div style="font-size:12px;font-weight:700;letter-spacing:0.16em;text-transform:uppercase;color:#ff8d7f;">
                  Live now
                </div>
              </div>
              <div style="display:flex;flex-direction:column;gap:6px;">
                <div style="font-size:23px;font-weight:700;line-height:1.2;letter-spacing:-0.01em;">
                  {live()!.title}
                </div>
                <Show when={live()!.educator}>
                  <div style="font-size:15px;color:#c9c9d3;">with {live()!.educator}</div>
                </Show>
              </div>
              <Show
                when={canJoin()}
                fallback={
                  <>
                    <div style="font-size:14px;color:#c9c9d3;line-height:1.5;">
                      Live access is for members. Upgrade to join the room.
                    </div>
                    <button onClick={openSchedule} style={GOLD_BTN}>
                      View schedule
                    </button>
                  </>
                }
              >
                <button onClick={openSchedule} style={GOLD_BTN}>
                  Join now
                </button>
              </Show>
            </div>
          </Show>
        </Show>
        </div>

        {/* coming up */}
        <div class="mj-card" data-card="comingup" style={cardStyle("comingup")} draggable={editing()} onDragStart={() => setDragId("comingup")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("comingup")}>
          {tools("comingup")}
        <Show when={later().length > 0}>
          <div style="display:flex;flex-direction:column;gap:10px;">
            <div style={HEAD}>Coming up</div>
            <For each={later()}>
              {(ev) => (
                <button
                  onClick={openSchedule}
                  style={RESET + "display:flex;align-items:center;gap:14px;padding:12px 14px;border-radius:16px;background:#1a1a20;border:1px solid #2a2a33;"}
                >
                  <div style="flex-shrink:0;width:50px;padding:7px 0;border-radius:12px;background:#241d0c;text-align:center;">
                    <div style="font-size:10px;font-weight:700;letter-spacing:0.12em;color:#E2B04A;">
                      {when(ev.t).mon}
                    </div>
                    <div style="font-size:20px;font-weight:700;color:#fff;line-height:1.1;">
                      {when(ev.t).day}
                    </div>
                  </div>
                  <div style="flex-grow:1;display:flex;flex-direction:column;gap:3px;">
                    <div style="font-size:15px;font-weight:600;line-height:1.3;">{ev.title}</div>
                    <div style={MUTED}>
                      {ev.educator ? ev.educator + " · " : ""}
                      {when(ev.t).time}
                    </div>
                  </div>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#8a8a98" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" style="flex-shrink:0;">
                    <path d="M9 6l6 6-6 6" />
                  </svg>
                </button>
              )}
            </For>
          </div>
        </Show>
        </div>

        </div>
        <div class="mj-col">
        {/* prayer times */}
        <div class="mj-card" data-card="prayer" style={cardStyle("prayer")} draggable={editing()} onDragStart={() => setDragId("prayer")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("prayer")}>
          {tools("prayer")}
        <div style={CARD}>
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
            <div style={OVER}>
              Prayer times{prayerCfg() ? " · " + prayerCfg()!.label : ""}
            </div>
            <Show when={prayerCfg()}>
              <button
                onClick={(e) => {
                  e.currentTarget.blur();
                  setPrayerOpen(!prayerOpen());
                }}
                aria-label="Prayer time settings"
                style={
                  RESET +
                  "display:flex;flex-shrink:0;width:30px;height:30px;margin-right:4px;border-radius:50%;outline:none;align-items:center;justify-content:center;transition:background .2s,border-color .2s;" +
                  (prayerOpen() ? "background:#2a2210;border:1px solid #FFC62E;" : "background:#23232b;border:1px solid transparent;")
                }
              >
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#c9c9d3" stroke-width="2" stroke-linecap="round" aria-hidden="true">
                  <path d="M4 6h9M17 6h3M4 12h3M11 12h9M4 18h11M19 18h1" />
                  <circle cx="15" cy="6" r="2" />
                  <circle cx="9" cy="12" r="2" />
                  <circle cx="17" cy="18" r="2" />
                </svg>
              </button>
            </Show>
          </div>

          <Show
            when={prayers()}
            fallback={
              <>
                <div style="font-size:18px;font-weight:600;">Your prayer times</div>
                <div style={MUTED}>
                  See the next prayer and a countdown, in your own time zone. Your location stays on this device.
                </div>
                <Show when={!prayerOpen()}>
                  <button onClick={useMyLocation} disabled={prayerBusy()} style={GOLD_BTN}>
                    {prayerBusy() ? "Finding you…" : "Use my location"}
                  </button>
                  <button
                    onClick={() => setPrayerOpen(true)}
                    style={RESET + "width:auto;background:none;border:none;padding:0;font-size:14px;font-weight:600;color:#FFC62E;"}
                  >
                    Or choose a city
                  </button>
                </Show>
              </>
            }
          >
            <div style="display:flex;flex-direction:column;gap:2px;">
              <div style="font-size:24px;font-weight:700;letter-spacing:-0.01em;">
                {prayers()!.next.key} in {countdown(prayers()!.next.t - now())}
              </div>
              <div style={MUTED}>
                {new Date(now()).toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })}
                {hijri() ? " · " + hijri() : ""}
              </div>
            </div>
            <div style="display:flex;flex-direction:column;gap:2px;">
              <For each={prayers()!.list}>
                {(p) => (
                  <div
                    style={
                      "display:flex;align-items:center;justify-content:space-between;padding:8px 10px;border-radius:10px;font-size:" +
                      (p.key === "Sunrise" ? "13px" : "15px") +
                      ";" +
                      (p.key === prayers()!.next.key
                        ? "background:#2a2210;color:#FFC62E;font-weight:700;"
                        : p.t < now() || p.key === "Sunrise"
                          ? "color:#8a8a96;"
                          : "color:#e6e6ee;")
                    }
                  >
                    <span>{p.key}</span>
                    <span>{prayerTime(p.t)}</span>
                  </div>
                )}
              </For>
            </div>
            <div style="font-size:12px;color:#77777f;line-height:1.4;">
              Calculated for your location. Please confirm with your local mosque.
            </div>
          </Show>

          <Show when={prayerOpen()}>
            <div style="display:flex;flex-direction:column;gap:10px;padding-top:6px;border-top:1px solid #23232b;">
              <button onClick={useMyLocation} disabled={prayerBusy()} style={GOLD_BTN}>
                {prayerBusy() ? "Finding you…" : "Use my location"}
              </button>
              <select style={SELECT} onChange={(e) => chooseCity(e.currentTarget.value)}>
                <option value="" selected>
                  Or choose a city…
                </option>
                <For each={PRAYER_CITIES}>{(c) => <option value={c.name}>{c.name}</option>}</For>
              </select>
              <Show when={prayerCfg()}>
                <div style={OVER}>Calculation method</div>
                <select style={SELECT} onChange={(e) => setPrayerOpt("method", e.currentTarget.value)}>
                  <For each={PRAYER_METHODS}>
                    {(m) => (
                      <option value={m[0]} selected={prayerCfg()!.method === m[0]}>
                        {m[1]}
                      </option>
                    )}
                  </For>
                </select>
                <div style={OVER}>Asr</div>
                <select style={SELECT} onChange={(e) => setPrayerOpt("madhab", e.currentTarget.value)}>
                  <option value="standard" selected={prayerCfg()!.madhab === "standard"}>
                    Standard (Shafi'i, Maliki, Hanbali)
                  </option>
                  <option value="hanafi" selected={prayerCfg()!.madhab === "hanafi"}>
                    Hanafi
                  </option>
                </select>
                <button
                  onClick={() => {
                    savePrayer(null);
                    setPrayerOpen(false);
                  }}
                  style={RESET + "width:auto;background:none;border:none;padding:0;font-size:13px;font-weight:600;color:#ff8a7a;"}
                >
                  Remove my location
                </button>
              </Show>
              <Show when={prayerErr()}>
                <div style="font-size:13px;color:#ff8a7a;">{prayerErr()}</div>
              </Show>
            </div>
          </Show>
        </div>
        </div>

        {/* announcement */}
        <div class="mj-card" data-card="announcement" style={cardStyle("announcement")} draggable={editing()} onDragStart={() => setDragId("announcement")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("announcement")}>
          {tools("announcement")}
        <Show when={announcement()}>
          <div style={CARD}>
            <div style="display:flex;align-items:center;justify-content:space-between;">
              <div style={OVER}>Latest announcement</div>
              <div style="font-size:12px;color:#a4a4b2;">{ago(announcement()!.at)}</div>
            </div>
            <div style="font-size:15px;line-height:1.55;color:#e6e6ee;display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;white-space:pre-line;">
              {announcement()!.text}
            </div>
            <button
              onClick={() => go(announcement()!.channel)}
              style={RESET + "width:auto;background:none;border:none;padding:0;font-size:14px;font-weight:600;color:#FFC62E;"}
            >
              Read more
            </button>
          </div>
        </Show>
        </div>

        {/* catch up */}
        <div class="mj-card" data-card="catchup" style={cardStyle("catchup")} draggable={editing()} onDragStart={() => setDragId("catchup")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("catchup")}>
          {tools("catchup")}
        <Show when={unreadList().length > 0}>
          <div style="display:flex;flex-direction:column;gap:10px;">
            <div style={HEAD}>Catch up</div>
            <div style="display:flex;flex-direction:column;gap:1px;border-radius:16px;overflow:hidden;background:#2a2a33;border:1px solid #2a2a33;">
              <For each={unreadList()}>
                {(item) => (
                  <button
                    onClick={() => go(item.c)}
                    style={RESET + "display:flex;align-items:center;gap:12px;padding:14px 16px;background:#1a1a20;border:none;"}
                  >
                    <div style="flex-grow:1;font-size:15px;font-weight:500;"># {item.name}</div>
                    <div style="min-width:24px;padding:3px 9px;border-radius:999px;background:#E2B04A;color:#1a1200;font-size:12px;font-weight:700;text-align:center;">
                      {item.mentions > 0 ? item.mentions : "New"}
                    </div>
                  </button>
                )}
              </For>
            </div>
          </div>
        </Show>
        </div>

        {/* for you */}
        <div class="mj-card" data-card="foryou" style={cardStyle("foryou")} draggable={editing()} onDragStart={() => setDragId("foryou")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("foryou")}>
          {tools("foryou")}
        <Show when={suggested()}>
          <button
            onClick={() => go(suggested()!.channel)}
            style={RESET + "display:flex;align-items:center;gap:14px;padding:16px 16px 16px 18px;border-radius:18px;background:#1a1a20;border:1px dashed #5a4716;"}
          >
            <div style="flex-grow:1;display:flex;flex-direction:column;gap:4px;">
              <div style={OVER}>For you</div>
              <div style="font-size:15px;font-weight:600;"># {suggested()!.channel.name}</div>
              <div style={MUTED}>Because you picked {suggested()!.topic}</div>
            </div>
            <Chevron />
          </button>
        </Show>
        </div>

        {/* help */}
        <div class="mj-card" data-tour="help" data-card="help" style={cardStyle("help")} draggable={editing()} onDragStart={() => setDragId("help")} onDragOver={(e) => { if (editing()) e.preventDefault(); }} onDrop={() => dropCard("help")}>
          {tools("help")}
        <button
          onClick={() => {
            const t = myTickets()[0];
            if (t) openTicketChannel(t.channel);
            else setHelpOpen(true);
          }}
          style={RESET + "display:flex;align-items:center;gap:14px;padding:16px 16px 16px 18px;border-radius:18px;background:#1a1a20;border:1px solid #2a2a33;"}
        >
          <div style="flex-shrink:0;width:42px;height:42px;border-radius:50%;background:#241d0c;border:1px solid #5a4716;display:flex;align-items:center;justify-content:center;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
              <path d="M21 12a8 8 0 0 1-11.6 7.1L4 20l1-4.3A8 8 0 1 1 21 12z" />
            </svg>
          </div>
          <div style="flex-grow:1;display:flex;flex-direction:column;gap:3px;">
            <Show
              when={myTickets()[0]}
              fallback={
                <>
                  <div style={OVER}>Support</div>
                  <div style="font-size:15px;font-weight:600;">Need help?</div>
                  <div style={MUTED}>Chat privately with the Majalis team</div>
                </>
              }
            >
              <div style={OVER}>Your ticket</div>
              <div style="font-size:15px;font-weight:600;">Ticket #{myTickets()[0].number} is open</div>
              <div style={MUTED}>Tap to continue the conversation</div>
            </Show>
          </div>
          <Chevron />
        </button>

        <Show when={helpOpen()}>
          <Portal>
          <div
            style="position:fixed;inset:0;z-index:100000;display:flex;align-items:center;justify-content:center;padding:16px;box-sizing:border-box;background:rgba(6,6,10,.55);backdrop-filter:blur(12px);-webkit-backdrop-filter:blur(12px);animation:mjfade .2s ease both;"
            onClick={(e) => {
              if (e.target === e.currentTarget) closeHelp();
            }}
          >
            <style>{"@keyframes mjfade{from{opacity:0}}@keyframes mjsheet{from{opacity:0;transform:translateY(24px) scale(.98)}}"}</style>
            <div style="width:100%;max-width:480px;max-height:92vh;max-height:92dvh;overflow:auto;box-sizing:border-box;padding:22px;border-radius:22px;background:#17171d;color:#fff;border:1px solid #3a2f10;box-shadow:0 20px 60px rgba(0,0,0,.6),0 0 40px rgba(255,184,28,.06);display:flex;flex-direction:column;gap:16px;animation:mjsheet .28s cubic-bezier(.22,1,.36,1) both;">
              <div style="display:flex;align-items:flex-start;justify-content:space-between;gap:12px;">
                <div style="display:flex;flex-direction:column;gap:4px;">
                  <div style={OVER}>Support</div>
                  <div style="font-size:22px;font-weight:700;">
                    {helpState() === "done" ? "We're on it" : "How can we help?"}
                  </div>
                </div>
                <button
                  onClick={closeHelp}
                  aria-label="Close"
                  style={RESET + "display:flex;flex-shrink:0;align-items:center;justify-content:center;width:34px;height:34px;margin-top:-2px;border-radius:50%;background:#26262e;border:1px solid #3a3a45;cursor:pointer;"}
                >
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#e6e6ee" stroke-width="2.4" stroke-linecap="round" aria-hidden="true">
                    <path d="M6 6l12 12M18 6L6 18" />
                  </svg>
                </button>
              </div>

              <Show
                when={helpState() !== "done"}
                fallback={
                  <>
                    <div style="display:flex;flex-direction:column;align-items:center;gap:10px;padding:14px 0 6px;text-align:center;">
                      <div style="width:56px;height:56px;border-radius:50%;background:#241d0c;border:1px solid #FFC62E;display:flex;align-items:center;justify-content:center;box-shadow:0 0 24px rgba(255,198,46,.25);">
                        <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                          <path d="M5 12.5l4.5 4.5L19 7.5" />
                        </svg>
                      </div>
                      <div style="font-size:18px;font-weight:700;">Ticket #{helpTicket()?.number} is open</div>
                      <div style={MUTED}>Your private chat with the Majalis team is ready. We'll reply there.</div>
                    </div>
                    <button
                      onClick={() => openTicketChannel(helpTicket()!.channel)}
                      style={RESET + "display:block;width:100%;box-sizing:border-box;text-align:center;padding:15px 20px;border-radius:13px;border:none;background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;font-size:16px;font-weight:700;cursor:pointer;"}
                    >
                      Open my ticket
                    </button>
                  </>
                }
              >
                <div style="display:flex;flex-wrap:wrap;gap:8px;">
                  <For each={HELP_CATS}>
                    {(c) => (
                      <button
                        onClick={() => setHelpCat(c.id)}
                        style={
                          RESET +
                          "padding:9px 14px;border-radius:999px;font-size:14px;font-weight:600;cursor:pointer;" +
                          (helpCat() === c.id
                            ? "background:#2a2210;border:1px solid #FFC62E;color:#FFC62E;"
                            : "background:#1f1f26;border:1px solid #2a2a33;color:#d7d7de;")
                        }
                      >
                        {c.label}
                      </button>
                    )}
                  </For>
                </div>

                <Show when={HELP_CATS.find((c) => c.id === helpCat())?.tip}>
                  <div style="display:flex;flex-direction:column;gap:4px;padding:12px 14px;border-radius:14px;background:#14141a;border:1px solid #23232b;">
                    <div style={OVER}>Quick answer</div>
                    <div style="font-size:14px;line-height:1.5;color:#c9c9d3;">
                      {HELP_CATS.find((c) => c.id === helpCat())?.tip}
                    </div>
                  </div>
                </Show>

                <div style="display:flex;flex-direction:column;gap:6px;">
                  <textarea
                    rows={5}
                    maxLength={1500}
                    placeholder="Tell us what's going on…"
                    value={helpMsg()}
                    onInput={(e) => setHelpMsg(e.currentTarget.value)}
                    style="width:100%;box-sizing:border-box;resize:vertical;padding:13px 14px;border-radius:14px;border:1px solid #2a2a33;background:#101015;color:#fff;font-size:15px;line-height:1.5;font-family:inherit;outline:none;"
                  />
                  <div style="display:flex;justify-content:space-between;gap:10px;font-size:12px;color:#8a8a96;">
                    <span>
                      {helpCat() === "membership"
                        ? "Billing tickets are seen only by the Majalis admins."
                        : "Only you and the Majalis team can see this."}
                    </span>
                    <span>{helpMsg().length}/1500</span>
                  </div>
                </div>

                <Show when={helpError()}>
                  <div style="font-size:14px;color:#ff8a7a;">{helpError()}</div>
                </Show>

                <button
                  disabled={!helpCat() || helpMsg().trim().length < 5 || helpState() === "sending"}
                  onClick={submitTicket}
                  style={
                    "display:block;width:100%;box-sizing:border-box;text-align:center;padding:15px 20px;border-radius:13px;border:none;font-size:16px;font-weight:700;font-family:inherit;" +
                    (!helpCat() || helpMsg().trim().length < 5 || helpState() === "sending"
                      ? "background:#2a2a33;color:#77777f;cursor:not-allowed;"
                      : "background:linear-gradient(135deg,#FFC62E,#F29D0A);color:#1a1200;cursor:pointer;")
                  }
                >
                  {helpState() === "sending" ? "Opening your ticket…" : "Send to the team"}
                </button>
              </Show>
            </div>
          </div>
          </Portal>
        </Show>
        </div>

        {/* footer */}
        <div style="display:flex;flex-direction:column;gap:14px;padding:16px 18px;border-radius:18px;background:#14141a;border:1px solid #23232b;">
          <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;">
            <div style="font-size:14px;color:#c9c9d3;">
              <Show when={pulse()} fallback="Majalis community">
                {pulse()!.members} members ·{" "}
                <Show when={pulse()!.online > 0}>
                  <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:#3ddc84;margin-right:6px;vertical-align:middle;box-shadow:0 0 8px rgba(61,220,132,0.6);" aria-hidden="true" />
                </Show>
                {pulse()!.online} online
              </Show>
            </div>
            <Show when={plan()}>
              <div style="padding:5px 12px;border-radius:999px;background:#241d0c;border:1px solid #5a4716;color:#FFC62E;font-size:12px;font-weight:700;">
                {plan()}
              </div>
            </Show>
          </div>
        </div>
        </div>
        <div
          class="mj-full"
          style="display:flex;flex-direction:column;align-items:center;gap:12px;padding:28px 12px 8px;text-align:center;"
        >
          <img
            src="/assets/web/salaam.svg"
            alt=""
            aria-hidden="true"
            style="width:110px;height:auto;opacity:0.6;"
          />
          <div style="max-width:420px;font-size:13px;line-height:1.6;color:#8a8a98;">
            Learn from educators. Grow in knowledge. Strengthen the Ummah for the sake of Allah.
          </div>
        </div>
        <Show when={!installed}>
          <div
            class="mj-full"
            style="display:flex;flex-direction:column;align-items:center;gap:8px;padding:0 12px 4px;text-align:center;"
          >
            <button
              onClick={install}
              style={RESET + "width:auto;display:flex;align-items:center;gap:6px;background:none;border:none;padding:4px 8px;font-size:12px;color:#8a8a98;text-decoration:underline;text-underline-offset:3px;"}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                <rect x="7" y="2.5" width="10" height="19" rx="2.5" />
                <path d="M11 18.5h2" />
              </svg>
              <span>{showHowTo() ? howTo : canPrompt() ? "Install the Majalis app" : "Get the Majalis app"}</span>
            </button>
          </div>
        </Show>
      </div>
    </div>
  );
}

function Chevron() {
  return (
    <div style="flex-shrink:0;width:42px;height:42px;border-radius:50%;background:#241d0c;border:1px solid #5a4716;display:flex;align-items:center;justify-content:center;">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#FFC62E" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
        <path d="M9 6l6 6-6 6" />
      </svg>
    </div>
  );
}
